/**
 * Token Manager - Bridge between AuthContext and API client
 *
 * This module provides a way for AuthContext to register token getter and refresh functions
 * that the API client can use without creating circular dependencies.
 *
 * Deduplicates concurrent refresh calls so that only one /refresh request is in-flight
 * at a time (prevents token rotation race conditions).
 */

type TokenGetter = () => string | null
type TokenRefresher = () => Promise<void>

let getToken: TokenGetter | null = null
let refreshToken: TokenRefresher | null = null

// Shared promise for deduplicating concurrent refresh attempts
let refreshPromise: Promise<void> | null = null

/**
 * Register token getter function from AuthContext
 */
export function registerTokenGetter(getter: TokenGetter) {
  getToken = getter
}

/**
 * Register token refresh function from AuthContext
 */
export function registerTokenRefresher(refresher: TokenRefresher) {
  refreshToken = refresher
}

/**
 * Get current access token
 */
export function getAccessToken(): string | null {
  return getToken ? getToken() : null
}

/**
 * Refresh access token (deduplicated — concurrent callers share one request)
 */
export async function refreshAccessToken(): Promise<void> {
  if (!refreshToken) {
    throw new Error('Token refresher not registered')
  }

  // If a refresh is already in-flight, piggyback on it
  if (refreshPromise) {
    return refreshPromise
  }

  refreshPromise = refreshToken().finally(() => {
    refreshPromise = null
  })

  return refreshPromise
}

